import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Maximize2 } from 'lucide-react';

interface VideoPreviewProps {
  videoUrl: string;
  showActions?: boolean;
  onDownload?: () => void;
  onFullScreen?: () => void;
}

/**
 * VideoPreview - HTML5 video player with controls
 *
 * Features:
 * - Native HTML5 video playback
 * - Download button
 * - Full-screen option
 * - Responsive layout
 */
export const VideoPreview: React.FC<VideoPreviewProps> = ({
  videoUrl,
  showActions = true,
  onDownload,
  onFullScreen
}) => {
  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      // Default download behavior
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `video-${Date.now()}.mp4`;
      link.click();
    }
  };

  const handleFullScreen = () => {
    if (onFullScreen) {
      onFullScreen();
    } else {
      // Default full-screen behavior
      window.open(videoUrl, '_blank');
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        {/* Video Player */}
        <div className="rounded-t-lg overflow-hidden bg-black">
          <video
            src={videoUrl}
            controls
            className="w-full h-auto max-h-[600px]"
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Action Buttons */}
        {showActions && (
          <div className="flex gap-2 p-4">
            <Button
              variant="outline"
              onClick={handleFullScreen}
              className="flex-1"
            >
              <Maximize2 className="mr-2 h-4 w-4" />
              Open Full Size
            </Button>
            <Button
              variant="outline"
              onClick={handleDownload}
              className="flex-1"
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VideoPreview;
